"use client"

import Image from "next/image"
import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"

export interface RelatedArticle {
  /** Localized blog-post slug for the current locale. */
  slug: string
  title: string
  excerpt: string
  imageUrl?: string
  imageAlt?: string
}

/**
 * "Related reading" strip — a small curated grid of blog-post cards, used to
 * feed topical-authority links from a product page into the insights cluster.
 * Purpose-built and self-contained (the /insights, /go and author-page card
 * variants are each local to their surface); consolidate onto this later.
 */
export function RelatedArticles({
  heading,
  articles,
}: {
  heading: string
  articles: RelatedArticle[]
}) {
  const lp = useLocalePath()
  if (articles.length === 0) return null

  return (
    <section
      data-testid="related-articles"
      className="lazy-section py-16 md:py-24"
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-10">
        <h2 className="type-display-sm text-neutral-900">{heading}</h2>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.slug}
              href={lp(`/insights/${a.slug}`)}
              className="group block overflow-hidden rounded-[16px] border border-neutral-200 bg-white transition-transform hover:-translate-y-1"
            >
              <div className="relative aspect-[16/10] w-full bg-neutral-100">
                {a.imageUrl && (
                  <Image
                    src={a.imageUrl}
                    alt={a.imageAlt || a.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover"
                  />
                )}
              </div>
              <div className="p-5">
                <h3 className="type-title-sm text-neutral-900">{a.title}</h3>
                <p className="type-body-sm mt-2 line-clamp-2 text-neutral-500">
                  {a.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
